import apiClient from './apiClient';

const handleError = (error) => {
  const message =
    error.response?.data?.error?.message ||
    error.response?.data?.message ||
    error.message;
  return message ? new Error(message) : error;
};

const invoicingService = {
  getActivation: async (year, month) => {
    try {
      const response = await apiClient.get('/admin/invoicing/activation', { params: { year, month } });
      return response.data.data;
    } catch (error) {
      throw handleError(error);
    }
  },

  updateActivation: async (year, month, payload) => {
    try {
      const response = await apiClient.put('/admin/invoicing/activation', payload, { params: { year, month } });
      return response.data;
    } catch (error) {
      throw handleError(error);
    }
  },

  // `range` ({ from, to }) narrows the preview to an explicit date interval —
  // Vikar Experten invoices week by week. year/month stay the activation
  // context, so they are always sent.
  getPreview: async (year, month, includePrevious, range) => {
    try {
      const response = await apiClient.get('/admin/invoicing/preview', {
        params: {
          year,
          month,
          includePrevious: includePrevious ? 'true' : 'false',
          ...(range && range.from && range.to ? { from: range.from, to: range.to } : {}),
        },
      });
      return response.data.data;
    } catch (error) {
      throw handleError(error);
    }
  },

  // Download the prepared push data as a CSV file (item 10).
  exportPreview: async (entryIds) => {
    try {
      const response = await apiClient.post('/admin/invoicing/export', { entryIds }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'economic-invoice-preview.csv';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      throw handleError(error);
    }
  },

  // Pre-transfer customer sync: match Tempus customers against e-conomic
  // (CVR first, then email). Longer timeout — it lists ALL e-conomic customers.
  syncPreview: async (customerIds) => {
    try {
      const response = await apiClient.post(
        '/admin/invoicing/sync-preview',
        { customerIds: customerIds || [] },
        { timeout: 60000 }
      );
      return response.data.data;
    } catch (error) {
      throw handleError(error);
    }
  },

  // Apply the admin's match decisions: link / create / skip per customer.
  // Never modifies existing e-conomic customers.
  syncApply: async (decisions) => {
    try {
      const response = await apiClient.post('/admin/invoicing/sync-apply', { decisions }, { timeout: 60000 });
      return response.data.data;
    } catch (error) {
      throw handleError(error);
    }
  },

  // One push resolves each customer in e-conomic and posts a draft invoice per
  // customer, so it can legitimately outlast the 10s default several times over.
  push: async (year, month, entryIds, range) => {
    try {
      const response = await apiClient.post(
        '/admin/invoicing/push',
        {
          year,
          month,
          entryIds,
          ...(range && range.from && range.to ? { from: range.from, to: range.to } : {}),
        },
        { timeout: 120000 }
      );
      return response.data.data;
    } catch (error) {
      throw handleError(error);
    }
  },
};

export default invoicingService;
